import React from "react";
import { Copy, KeyRound, UserCog } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { useCurrentUser } from "@modules/authentication/useCurrentUser";
import { UserRole, hasRequiredRole } from "@modules/authentication/roles";
import { useInjection } from "@di/DependencyContext";
import { DependencyType } from "@di/DependencyType";
import { UserService } from "@modules/user/user.service";
import { UsersTable } from "@modules/user/components/UsersTable";
import { useUsersTable } from "@modules/user/useUsersTable";
import type { UserViewModel } from "@modules/user/userViewModel";
import { Routes } from "~/routes";
import { toast } from "sonner";
import {
  PASSWORD_POLICY_DESCRIPTION,
  getPasswordPolicyErrors,
} from "@modules/authentication/passwordPolicy";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";

const AdminUsersPage = () => {
  const { currentUser, loading } = useCurrentUser();
  const table = useUsersTable();
  const [impersonateDialogUser, setImpersonateDialogUser] =
    React.useState<UserViewModel | null>(null);
  const [impersonateToken, setImpersonateToken] = React.useState<string | null>(
    null
  );
  const [isImpersonating, setIsImpersonating] = React.useState(false);
  const [setPasswordDialogUser, setSetPasswordDialogUser] =
    React.useState<UserViewModel | null>(null);
  const [adminPassword, setAdminPassword] = React.useState("");
  const [isSettingPassword, setIsSettingPassword] = React.useState(false);

  const userService = useInjection<UserService>(DependencyType.UserService);
  const navigate = useNavigate();

  const isSuperAdmin = hasRequiredRole(currentUser?.role ?? UserRole.User, [
    UserRole.SuperAdmin,
  ]);

  React.useEffect(() => {
    if (
      !loading &&
      !hasRequiredRole(currentUser?.role ?? UserRole.User, [
        UserRole.Admin,
        UserRole.SuperAdmin,
      ])
    ) {
      navigate(Routes.Home);
    }
  }, [loading, currentUser, navigate]);

  const handleImpersonate = React.useCallback(
    async (user: UserViewModel) => {
      setImpersonateDialogUser(user);
      setImpersonateToken(null);
      setIsImpersonating(true);

      try {
        const token = await userService.impersonateUser(user.id);
        setImpersonateToken(token);
      } catch (error) {
        console.error("Failed to impersonate user:", error);
        toast.error(
          `Failed to impersonate user: ${error instanceof Error ? error.message : "Unknown error"}`
        );
        setImpersonateDialogUser(null);
      } finally {
        setIsImpersonating(false);
      }
    },
    [userService]
  );

  const handleCopyToken = React.useCallback(async () => {
    if (!impersonateToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(impersonateToken);
      toast.success("Token copied to clipboard");
    } catch {
      toast.error("Failed to copy token to clipboard");
    }
  }, [impersonateToken]);

  const resetAdminPasswordDialog = React.useCallback(() => {
    setSetPasswordDialogUser(null);
    setAdminPassword("");
  }, []);

  const passwordPolicyErrors = React.useMemo(
    () => getPasswordPolicyErrors(adminPassword),
    [adminPassword]
  );
  const canSetAdminPassword =
    setPasswordDialogUser !== null &&
    passwordPolicyErrors.length === 0 &&
    !isSettingPassword;

  const handleAdminSetPassword = React.useCallback(async () => {
    if (!setPasswordDialogUser || !canSetAdminPassword) {
      return;
    }

    setIsSettingPassword(true);
    try {
      await userService.adminSetUserPassword(
        setPasswordDialogUser.id,
        adminPassword
      );
      toast.success("Password updated");
      resetAdminPasswordDialog();
    } catch (error) {
      toast.error(
        `Failed to set password: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsSettingPassword(false);
    }
  }, [
    adminPassword,
    canSetAdminPassword,
    resetAdminPasswordDialog,
    setPasswordDialogUser,
    userService,
  ]);

  return (
    <PageTemplate
      title="Users"
      breadcrumbs={[
        { label: "Admin", path: "/admin" },
        { label: "Users", path: "/admin/users" },
      ]}
      subtitle="View and administrate users of the PES ScentsCheck platform"
      activeItem="Admin"
      activeSubItem="Users"
      headerRule
    >
      <UsersTable
        table={table}
        renderActions={
          isSuperAdmin
            ? (user) => (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleImpersonate(user);
                    }}
                  >
                    <UserCog className="size-4" />
                    Impersonate
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5"
                    onClick={(event) => {
                      event.stopPropagation();
                      setSetPasswordDialogUser(user);
                    }}
                  >
                    <KeyRound className="size-4" />
                    Set password
                  </Button>
                </div>
              )
            : undefined
        }
      />
      <Dialog
        open={impersonateDialogUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            setImpersonateDialogUser(null);
            setImpersonateToken(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impersonate User</DialogTitle>
            <DialogDescription>
              {impersonateDialogUser
                ? `JWT token for ${impersonateDialogUser.fullName ?? impersonateDialogUser.email ?? "user"}`
                : "Generating token..."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isImpersonating ? (
              <p className="text-sm text-muted-foreground">
                Generating token...
              </p>
            ) : impersonateToken ? (
              <div className="relative">
                <pre className="max-h-40 overflow-auto rounded-md border bg-muted p-3 text-xs break-all whitespace-pre-wrap">
                  {impersonateToken}
                </pre>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            {impersonateToken && (
              <Button
                type="button"
                onClick={handleCopyToken}
                className="gap-1.5"
              >
                <Copy className="size-4" />
                Copy Token
              </Button>
            )}
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={setPasswordDialogUser !== null}
        onOpenChange={(open) => {
          if (!open) {
            resetAdminPasswordDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Password</DialogTitle>
            <DialogDescription>
              {setPasswordDialogUser
                ? `Set a password for ${setPasswordDialogUser.fullName ?? setPasswordDialogUser.email ?? "this user"}.`
                : "Set a password for this user."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={adminPassword}
                onChange={(event) => setAdminPassword(event.target.value)}
                disabled={isSettingPassword}
              />
              <p className="text-xs text-muted-foreground">
                {PASSWORD_POLICY_DESCRIPTION}
              </p>
              {adminPassword.length > 0 && passwordPolicyErrors.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-xs text-destructive">
                  {passwordPolicyErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => void handleAdminSetPassword()}
              disabled={!canSetAdminPassword}
            >
              Set password
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={resetAdminPasswordDialog}
              disabled={isSettingPassword}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTemplate>
  );
};

export default AdminUsersPage;
